import { Temporal } from 'temporal-polyfill';

/**
 * Type guard to check if a value is a Temporal.ZonedDateTime.
 *
 * @param value - The value to check
 * @returns True if the value is a Temporal.ZonedDateTime
 */
export function isZonedDateTime(value: unknown): value is Temporal.ZonedDateTime {
	return value instanceof Temporal.ZonedDateTime;
}

export type TemporalValue = Temporal.PlainDateTime
	| Temporal.PlainDate
	| Temporal.PlainTime
	| Temporal.Instant
	| Temporal.ZonedDateTime;

/**
 * Type guard to check if a value is a Temporal type.
 *
 * @param value - The value to check
 * @returns True if the value is a Temporal.PlainDateTime, PlainDate, PlainTime, Instant, or ZonedDateTime
 */
export function isTemporal(value: unknown): value is TemporalValue {
	return (
		value instanceof Temporal.PlainDateTime
		|| value instanceof Temporal.PlainDate
		|| value instanceof Temporal.PlainTime
		|| value instanceof Temporal.Instant
		|| value instanceof Temporal.ZonedDateTime
	);
}

/**
 * Returns the constructor/type of a Temporal value.
 *
 * This allows you to get the type constructor from an instance, which is useful for
 * operations like `compare()` that are static methods on the type.
 *
 * Note: It feels like there should be a built-in way to do this in the Temporal API,
 * but the specification doesn't provide a unified method to retrieve the constructor
 * from an instance. This utility fills that gap.
 *
 * @param value - A Temporal value (PlainDateTime, PlainDate, PlainTime, Instant, or ZonedDateTime)
 * @returns The constructor/type of the Temporal value, or undefined if the type is not recognized
 */
export function getTemporalType(value: TemporalValue) {
	if (value instanceof Temporal.PlainDateTime) return Temporal.PlainDateTime;
	if (value instanceof Temporal.PlainDate) return Temporal.PlainDate;
	if (value instanceof Temporal.PlainTime) return Temporal.PlainTime;
	if (value instanceof Temporal.Instant) return Temporal.Instant;
	if (value instanceof Temporal.ZonedDateTime) return Temporal.ZonedDateTime;
}
